import { useEffect, useState } from "react";
import RichTextEditor, { RichTextDisplay } from "./RichTextEditor";
import {
  createBroadcast,
  updateBroadcast,
  deactivateBroadcast,
  deleteBroadcast,
  getAllBroadcasts,
  getResponsesForBroadcast,
  reactToResponse,
  replyToResponse,
} from "./broadcasts";
import { db } from "./firebase";
import { doc, getDoc, setDoc, updateDoc, Timestamp } from "firebase/firestore";
import { createCall, createDirectCall, finalizeCall, createDailyRoom, deleteDailyRoom, endCall, notifyGroupOfCall, formatCallTime, getCallStatus } from "./calls";
import { writeUserNotification } from "./userNotifications";

const TYPES = [
  { key: "announcement", label: "Announcement", emoji: "📢", desc: "Clients tap 'Read' and it disappears" },
  { key: "question",     label: "Question",     emoji: "💬", desc: "Clients leave a comment" },
  { key: "poll",         label: "Poll",         emoji: "📊", desc: "Clients pick from options you set" },
  { key: "note",         label: "Note",         emoji: "📌", desc: "Pinned note shown on clients' dashboards" },
  { key: "call_poll",    label: "Call Poll",    emoji: "📹", desc: "Poll your group for the best call time, then schedule a video call" },
];

const card = { background: "#f8fbff", borderRadius: "18px", border: "2px solid #bfdbfe", padding: "18px" };
const labelStyle = { color: "#4b6b8a", fontSize: "12px", fontWeight: "bold", textTransform: "uppercase", marginBottom: "6px" };
const inputStyle = { width: "100%", boxSizing: "border-box", padding: "10px 14px", borderRadius: "12px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontSize: "14px", outline: "none" };
const btnPrimary = { padding: "10px 18px", borderRadius: "14px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", cursor: "pointer", fontSize: "14px" };
const btnSecondary = { padding: "8px 14px", borderRadius: "12px", border: "2px solid #bfdbfe", background: "#f8fbff", color: "#1e3a5f", fontWeight: "bold", cursor: "pointer", fontSize: "13px" };

function formatDate(ts) {
  if (!ts) return "";
  const d = ts.toDate?.() || new Date(ts);
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

export default function AdminBroadcasts({ adminUid, allUsers, adminName = "", adminAvatar = "", programOptions = [], groupCall = null, initialType = null, initialTypeKey = 0 }) {
  // Merge defined programs with any extra tags found on actual users
  const userTags = [...new Set((allUsers || []).map((u) => u.programTag).filter(Boolean))];
  const definedTags = programOptions.map((p) => p.value || p.tag).filter(Boolean);
  const programTags = [...new Set([...definedTags, ...userTags])];

  const [selectedProgram, setSelectedProgram] = useState("all");
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [responses, setResponses] = useState([]);
  const [responsesLoading, setResponsesLoading] = useState(false);

  // Form state
  const [formProgram, setFormProgram] = useState("");
  const [type, setType] = useState("announcement");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [xpEnabled, setXpEnabled] = useState(false);
  const [xpReward, setXpReward] = useState(20);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  // Call poll state
  const [callTimeSlots, setCallTimeSlots] = useState(["", ""]);
  const [callFinalizingSlot, setCallFinalizingSlot] = useState(null);
  const [callFinalizeStatus, setCallFinalizeStatus] = useState("");

  // Audience targeting for broadcasts
  const [audienceType, setAudienceType] = useState("program");
  const [targetUserIds, setTargetUserIds] = useState([]);
  const [targetUserSearch, setTargetUserSearch] = useState("");

  // Schedule call (direct, no poll)
  const [showScheduleCall, setShowScheduleCall] = useState(false);
  const [scTitle, setScTitle] = useState("");
  const [scWhen, setScWhen] = useState("now");
  const [scDateTime, setScDateTime] = useState("");
  const [scEndDateTime, setScEndDateTime] = useState("");
  const [scAudience, setScAudience] = useState("program");
  const [scProgram, setScProgram] = useState("");
  const [scTargetUserIds, setScTargetUserIds] = useState([]);
  const [scTargetUserSearch, setScTargetUserSearch] = useState("");
  const [scSaving, setScSaving] = useState(false);
  const [scStatus, setScStatus] = useState("");
  const [scRoomUrl, setScRoomUrl] = useState("");
  const [scScreenshare, setScScreenshare] = useState("everyone");
  const [endingLiveCall, setEndingLiveCall] = useState(false);

  // Note-type state
  const today = new Date().toISOString().slice(0, 10);
  const nextWeek = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const [noteStartDate, setNoteStartDate] = useState(today);
  const [noteEndDate, setNoteEndDate] = useState(nextWeek);
  const [noteStartTime, setNoteStartTime] = useState("09:00");
  const [noteEndTime, setNoteEndTime] = useState("21:00");
  const [noteTargetType, setNoteTargetType] = useState("all");
  const [noteTargetProgram, setNoteTargetProgram] = useState("");
  const [noteTargetUserId, setNoteTargetUserId] = useState("");
  const [noteTargetUserName, setNoteTargetUserName] = useState("");
  const [noteUserSearch, setNoteUserSearch] = useState("");
  const [noteLinkLabel, setNoteLinkLabel] = useState("");
  const [noteLinkUrl, setNoteLinkUrl] = useState("");
  const [notes, setNotes] = useState([]);

  const noteUserResults = noteUserSearch.trim().length >= 1
    ? (allUsers || []).filter((u) => (u.name || "").toLowerCase().includes(noteUserSearch.toLowerCase())).slice(0, 8)
    : [];

  const targetUserResults = audienceType === "users" && targetUserSearch.trim().length >= 1
    ? (allUsers || []).filter((u) => !targetUserIds.includes(u.id) && (u.name || "").toLowerCase().includes(targetUserSearch.toLowerCase())).slice(0, 8)
    : [];
  const scUserResults = scTargetUserSearch.trim().length >= 1
    ? (allUsers || []).filter((u) => !scTargetUserIds.includes(u.id) && (u.name || "").toLowerCase().includes(scTargetUserSearch.toLowerCase())).slice(0, 8)
    : [];

  useEffect(() => {
    getDoc(doc(db, "appConfig", "pinnedNotes")).then((snap) => {
      if (snap.exists() && Array.isArray(snap.data().notes)) setNotes(snap.data().notes);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!initialType || !initialTypeKey) return;
    setType(initialType);
    setShowForm(true);
    setTimeout(() => document.getElementById("admin-broadcasts-form")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
  }, [initialTypeKey]);

  async function saveNote() {
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    const entry = {
      id: `note-${Date.now()}`,
      title: title.trim(),
      body: body.trim(),
      startDate: noteStartDate,
      startTime: noteStartTime,
      endDate: noteEndDate,
      endTime: noteEndTime,
      targetType: noteTargetType,
      targetProgram: noteTargetType === "program" ? noteTargetProgram : "all",
      targetUserId: noteTargetType === "user" ? noteTargetUserId : "",
      targetUserName: noteTargetType === "user" ? noteTargetUserName : "",
      linkLabel: noteLinkLabel,
      linkUrl: noteLinkUrl,
    };
    const updated = [...notes, entry];
    await setDoc(doc(db, "appConfig", "pinnedNotes"), { notes: updated });
    setNotes(updated);
    setTitle(""); setBody(""); setShowForm(false);
    setNoteStartDate(today); setNoteEndDate(nextWeek);
    setNoteStartTime("00:00"); setNoteEndTime("23:59");
    setNoteTargetType("all"); setNoteTargetProgram(""); setNoteTargetUserId(""); setNoteTargetUserName(""); setNoteUserSearch("");
    setNoteLinkLabel(""); setNoteLinkUrl("");
    setSaving(false);
  }

  async function deleteNote(id) {
    const updated = notes.filter((n) => n.id !== id);
    await setDoc(doc(db, "appConfig", "pinnedNotes"), { notes: updated });
    setNotes(updated);
  }

  // Per-response reply state: { [responseId]: { text, saving, open } }
  const [replyState, setReplyState] = useState({});

  function setReply(responseId, patch) {
    setReplyState((prev) => ({ ...prev, [responseId]: { ...prev[responseId], ...patch } }));
  }

  async function handleReact(responseId, emoji) {
    const response = responses.find((r) => r.id === responseId);
    const next = response?.adminReaction === emoji ? null : emoji;
    await reactToResponse(responseId, next, {
      toUserId: response?.userId || "",
      broadcastTitle: selectedBroadcast?.title || "",
      fromName: adminName,
      fromAvatar: adminAvatar,
    });
    setResponses((prev) => prev.map((r) => r.id === responseId ? { ...r, adminReaction: next } : r));
  }

  async function handleReply(responseId) {
    const text = replyState[responseId]?.text ?? "";
    if (!text.trim()) return;
    setReply(responseId, { saving: true });
    const response = responses.find((r) => r.id === responseId);
    try {
      await replyToResponse(responseId, text, {
        toUserId: response?.userId || "",
        broadcastTitle: selectedBroadcast?.title || "",
        fromName: adminName,
        fromAvatar: adminAvatar,
      });
      setResponses((prev) => prev.map((r) => r.id === responseId ? { ...r, adminReply: text.trim() } : r));
      setReply(responseId, { saving: false, open: false });
    } catch {
      setReply(responseId, { saving: false });
    }
  }

  async function handleDeleteReply(responseId) {
    await replyToResponse(responseId, "");
    setResponses((prev) => prev.map((r) => r.id === responseId ? { ...r, adminReply: "" } : r));
    setReply(responseId, { text: "", open: false });
  }

  useEffect(() => {
    loadBroadcasts();
  }, []);

  async function loadBroadcasts() {
    setLoading(true);
    try {
      setBroadcasts(await getAllBroadcasts());
    } finally {
      setLoading(false);
    }
  }

  const filteredBroadcasts = selectedProgram === "all"
    ? broadcasts
    : broadcasts.filter((b) => b.programTag === selectedProgram);

  async function openBroadcast(b) {
    setSelectedBroadcast(b);
    setResponsesLoading(true);
    try {
      setResponses(await getResponsesForBroadcast(b.id));
    } finally {
      setResponsesLoading(false);
    }
  }

  async function handleScheduleCall() {
    if (!scTitle.trim()) return;
    if (scWhen === "future" && !scDateTime) return;
    if (scAudience === "program" && !scProgram) return;
    if (scAudience === "users" && scTargetUserIds.length === 0) return;
    setScSaving(true);
    setScStatus("Creating video room...");
    try {
      const scheduledTime = scWhen === "now" ? new Date().toISOString() : scDateTime;
      const programTag = scAudience === "users"
        ? ((allUsers || []).find((u) => scTargetUserIds.includes(u.id))?.programTag || "")
        : scProgram;
      const callId = await createDirectCall({ programTag, title: scTitle, targetType: scAudience, targetUserIds: scAudience === "users" ? scTargetUserIds : [], scheduledTime, scheduledEndTime: scEndDateTime || null, createdBy: adminUid });
      const { roomUrl, roomName } = await createDailyRoom(callId, scheduledTime, { screenshareMode: scScreenshare });
      await updateDoc(doc(db, "calls", callId), { roomUrl, roomName, scheduledEndTime: scEndDateTime ? Timestamp.fromDate(new Date(scEndDateTime)) : null });
      setScRoomUrl(roomUrl);
      setScStatus("Notifying users...");
      const timeLabel = formatCallTime(scheduledTime);
      const msg = scWhen === "now" ? "A call is starting now! Join in the Calls tab." : ("Call scheduled: " + timeLabel + ". Join in the Calls tab.");
      if (scAudience === "program") {
        await notifyGroupOfCall(programTag, callId, scheduledTime);
      } else {
        await Promise.all(scTargetUserIds.map((uid) =>
          writeUserNotification({ toUserId: uid, type: "call_scheduled", message: msg, fromName: adminName || "Your Coach", fromAvatar: adminAvatar || "", postId: callId, screen: "calls" })
        ));
      }
      setScStatus(scWhen === "now" ? "Call is live — tap below to join." : ("Scheduled for " + timeLabel + ". Users notified."));
      setScTitle(""); setScWhen("now"); setScDateTime(""); setScAudience("program"); setScProgram(""); setScTargetUserIds([]); setScTargetUserSearch("");
    } catch (e) {
      setScStatus("Error: " + e.message);
    }
    setScSaving(false);
  }

  async function handleCallFinalize(opt) {
    if (!selectedBroadcast?.callId) return;
    setCallFinalizingSlot(opt);
    setCallFinalizeStatus("Creating room...");
    try {
      const { roomUrl, roomName } = await createDailyRoom(selectedBroadcast.callId, opt);
      await finalizeCall(selectedBroadcast.callId, { scheduledTime: opt, roomUrl, roomName, winnerOption: opt });
      await notifyGroupOfCall(selectedBroadcast.programTag, selectedBroadcast.callId, opt);
      setCallFinalizeStatus("Done! Call scheduled and users notified.");
    } catch (e) {
      setCallFinalizeStatus("Error: " + e.message);
    }
    setCallFinalizingSlot(null);
  }

  async function handleCreate() {
    if (!title.trim() || !body.trim()) return;
    if (type === "poll" && options.filter((o) => o.trim()).length < 2) return;
    if (type === "call_poll" && callTimeSlots.filter((s) => s.trim()).length < 2) return;
    if (audienceType === "users" && targetUserIds.length === 0) return;
    setSaving(true);
    try {
      const programTag = selectedProgram === "all" ? formProgram : selectedProgram;
      let callId = null;
      if (type === "call_poll") {
        callId = await createCall({
          programTag,
          title,
          pollBroadcastId: null,
          createdBy: adminUid,
          targetType: audienceType,
          targetUserIds: audienceType === "users" ? targetUserIds : [],
        });
      }
      const broadcastRef = await createBroadcast({
        type,
        title,
        body,
        programTag,
        options: type === "poll" ? options.filter((o) => o.trim()) : type === "call_poll" ? callTimeSlots.filter((s) => s.trim()) : [],
        allowMultiple: type === "poll" ? allowMultiple : false,
        xpReward: xpEnabled ? xpReward : 0,
        adminUid,
        callId,
        targetType: audienceType,
        targetUserIds: audienceType === "users" ? targetUserIds : [],
      });
      if (callId && broadcastRef?.id) {
        await updateDoc(doc(db, "calls", callId), { pollBroadcastId: broadcastRef.id });
      }
      setShowForm(false);
      setTitle(""); setBody(""); setType("announcement"); setOptions(["", ""]); setAllowMultiple(false);
      setXpEnabled(false); setXpReward(20); setFormProgram("");
      setCallTimeSlots(["", ""]);
      setAudienceType("program"); setTargetUserIds([]); setTargetUserSearch("");
      await loadBroadcasts();
    } finally {
      setSaving(false);
    }
  }

  function openEditMode(b) {
    setEditDraft({
      title: b.title || "",
      body: b.body || "",
      options: b.options?.length ? [...b.options] : ["", ""],
      allowMultiple: b.allowMultiple || false,
      xpReward: b.xpReward || 0,
    });
    setEditMode(true);
  }

  async function handleEditSave(b) {
    if (!editDraft) return;
    setEditSaving(true);
    try {
      await updateBroadcast(b.id, editDraft);
      const updated = { ...b, ...editDraft };
      setSelectedBroadcast(updated);
      setBroadcasts((prev) => prev.map((x) => x.id === b.id ? updated : x));
      setEditMode(false);
      setEditDraft(null);
    } finally {
      setEditSaving(false);
    }
  }

  async function handleDeactivate(id) {
    await deactivateBroadcast(id);
    await loadBroadcasts();
    if (selectedBroadcast?.id === id) setSelectedBroadcast((b) => ({ ...b, isActive: false }));
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this broadcast? This cannot be undone.")) return;
    await deleteBroadcast(id);
    setSelectedBroadcast(null);
    await loadBroadcasts();
  }

  // ── Response detail view ─────────────────────────────────────────────────

  if (selectedBroadcast) {
    const b = selectedBroadcast;
    const typeInfo = TYPES.find((t) => t.key === b.type);

    // Poll tally
    const tally = {};
    if (b.type === "poll" || b.type === "call_poll") {
      for (const option of b.options) tally[option] = 0;
      for (const r of responses) {
        for (const sel of r.selections || []) {
          tally[sel] = (tally[sel] || 0) + 1;
        }
      }
    }

    const programUserCount = (allUsers || []).filter((u) => u.programTag === b.programTag).length;

    return (
      <div style={{ display: "grid", gap: "14px" }}>
        <button onClick={() => { setSelectedBroadcast(null); setEditMode(false); setEditDraft(null); }} style={{ ...btnSecondary, alignSelf: "flex-start" }}>← Back</button>

        <div style={card}>
          {editMode && editDraft ? (
            <div style={{ display: "grid", gap: "12px" }}>
              <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>Edit Broadcast</div>
              <div>
                <div style={labelStyle}>Title</div>
                <input value={editDraft.title} onChange={(e) => setEditDraft((d) => ({ ...d, title: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <div style={labelStyle}>{b.type === "question" ? "Question" : b.type === "poll" ? "Question / Instructions" : "Message"}</div>
                <RichTextEditor value={editDraft.body} onChange={(v) => setEditDraft((d) => ({ ...d, body: v }))} rows={4} />
              </div>
              {b.type === "poll" && (
                <div>
                  <div style={labelStyle}>Options</div>
                  <div style={{ display: "grid", gap: "8px" }}>
                    {editDraft.options.map((opt, i) => (
                      <div key={i} style={{ display: "flex", gap: "8px" }}>
                        <input value={opt} onChange={(e) => { const next = [...editDraft.options]; next[i] = e.target.value; setEditDraft((d) => ({ ...d, options: next })); }} placeholder={`Option ${i + 1}`} style={{ ...inputStyle, flex: 1 }} />
                        {editDraft.options.length > 2 && (
                          <button onClick={() => setEditDraft((d) => ({ ...d, options: d.options.filter((_, j) => j !== i) }))} style={{ ...btnSecondary, color: "#38bdf8", borderColor: "#38bdf8" }}>✕</button>
                        )}
                      </div>
                    ))}
                    <button onClick={() => setEditDraft((d) => ({ ...d, options: [...d.options, ""] }))} style={{ ...btnSecondary, alignSelf: "flex-start" }}>+ Add option</button>
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px", cursor: "pointer", color: "#1e3a5f", fontSize: "14px" }}>
                    <input type="checkbox" checked={editDraft.allowMultiple} onChange={(e) => setEditDraft((d) => ({ ...d, allowMultiple: e.target.checked }))} />
                    Allow selecting multiple answers
                  </label>
                </div>
              )}
              <div>
                <div style={labelStyle}>XP Reward</div>
                <input type="number" min="0" value={editDraft.xpReward} onChange={(e) => setEditDraft((d) => ({ ...d, xpReward: Number(e.target.value) }))} style={inputStyle} />
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={() => handleEditSave(b)} disabled={editSaving} style={{ ...btnPrimary, opacity: editSaving ? 0.6 : 1 }}>{editSaving ? "Saving..." : "Save changes"}</button>
                <button onClick={() => { setEditMode(false); setEditDraft(null); }} style={btnSecondary}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "18px" }}>{typeInfo?.emoji}</span>
                <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px", flex: 1, minWidth: 0 }}>{b.title}</span>
                <span style={{ fontSize: "11px", background: b.isActive ? "#e0f2fe" : "#dbeafe", color: "#4b6b8a", padding: "2px 8px", borderRadius: "999px", border: "2px solid #bfdbfe", whiteSpace: "nowrap" }}>
                  {b.isActive ? "Active" : "Closed"}
                </span>
              </div>
              <RichTextDisplay html={b.body} style={{ color: "#4b6b8a", fontSize: "13px", marginBottom: "12px" }} />
              <div style={{ color: "#6b8cb0", fontSize: "12px", marginBottom: "12px" }}>
                Sent {formatDate(b.createdAt)} · {b.programTag} · {responses.length} / {programUserCount} responded
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button onClick={() => openEditMode(b)} style={btnSecondary}>✏️ Edit</button>
                {b.isActive && (
                  <button onClick={() => handleDeactivate(b.id)} style={btnSecondary}>Close</button>
                )}
                <button onClick={() => handleDelete(b.id)} style={{ ...btnSecondary, color: "#38bdf8", borderColor: "#38bdf8" }}>Delete</button>
              </div>
            </>
          )}
        </div>

        {responsesLoading ? (
          <div style={{ color: "#4b6b8a", padding: "16px" }}>Loading responses...</div>
        ) : responses.length === 0 ? (
          <div style={{ ...card, color: "#4b6b8a" }}>No responses yet.</div>
        ) : (
          <>
            {/* Poll / Call Poll results */}
            {(b.type === "poll" || b.type === "call_poll") && (
              <div style={{ display: "grid", gap: "14px" }}>
                {b.options.map((opt) => {
                  const count = tally[opt] || 0;
                  const pct = responses.length > 0 ? Math.round((count / responses.length) * 100) : 0;
                  const voters = responses.filter((r) => (r.selections || []).includes(opt));
                  const starCount = b.type === "call_poll" ? voters.filter((r) => r.starred === opt).length : 0;
                  return (
                    <div key={opt} style={card}>
                      {/* Option label + bar */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "6px" }}>
                        <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>{b.type === "call_poll" ? formatCallTime(opt) : opt}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {starCount > 0 && (
                            <span style={{ fontSize: "12px", color: "#b45309", background: "#fef3c7", borderRadius: "999px", padding: "2px 8px", fontWeight: "bold" }}>
                              ⭐ {starCount} preferred
                            </span>
                          )}
                          <span style={{ color: "#4b6b8a", fontSize: "13px" }}>{count} vote{count !== 1 ? "s" : ""} · {pct}%</span>
                        </div>
                      </div>
                      <div style={{ background: "#bfdbfe", borderRadius: "999px", height: "10px", overflow: "hidden", marginBottom: "12px" }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg, #38bdf8, #2563eb)", borderRadius: "999px", transition: "width 0.4s ease" }} />
                      </div>
                      {/* Avatars + names of who picked this */}
                      {voters.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                          {voters.map((r) => {
                            const didStar = b.type === "call_poll" && r.starred === opt;
                            return (
                              <div key={r.id} style={{ display: "flex", alignItems: "center", gap: "6px", background: didStar ? "#fef9c3" : "#e0f2fe", borderRadius: "999px", padding: "4px 10px 4px 4px", border: `2px solid ${didStar ? "#fde68a" : "#bfdbfe"}` }}>
                                {r.userAvatar ? (
                                  <img src={r.userAvatar} alt={r.userName} style={{ width: "24px", height: "24px", borderRadius: "999px", objectFit: "cover", flexShrink: 0 }} />
                                ) : (
                                  <div style={{ width: "24px", height: "24px", borderRadius: "999px", background: "#bfdbfe", flexShrink: 0 }} />
                                )}
                                <span style={{ fontSize: "12px", fontWeight: "bold", color: "#1e3a5f" }}>{r.userName || "Client"}</span>
                                {didStar && <span style={{ fontSize: "13px" }}>⭐</span>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {b.type === "call_poll" && (
                        <button
                          onClick={() => handleCallFinalize(opt)}
                          disabled={!!callFinalizingSlot}
                          style={{ marginTop: "8px", padding: "7px 14px", borderRadius: "10px", border: "2px solid #2563eb", background: callFinalizingSlot === opt ? "#2563eb" : "transparent", color: callFinalizingSlot === opt ? "#fff" : "#2563eb", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}
                        >
                          {callFinalizingSlot === opt ? "Setting up..." : "✓ Use this time"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {b.type === "call_poll" && callFinalizeStatus && (
              <div style={{ padding: "10px 14px", borderRadius: "10px", background: callFinalizeStatus.startsWith("Error") ? "#fef2f2" : "#ecfdf5", border: "2px solid " + (callFinalizeStatus.startsWith("Error") ? "#fca5a5" : "#6ee7b7"), color: callFinalizeStatus.startsWith("Error") ? "#dc2626" : "#059669", fontWeight: "bold", fontSize: "13px" }}>
                {callFinalizeStatus}
              </div>
            )}

            {/* Announcement / Question individual responses */}
            {b.type !== "poll" && b.type !== "call_poll" && (
              <div style={{ display: "grid", gap: "10px" }}>
                {responses.map((r) => {
                  const rs = replyState[r.id] || {};
                  const replyText = rs.text ?? (r.adminReply || "");
                  const replyOpen = rs.open ?? false;
                  const REACTIONS = ["❤️", "🔥", "💪", "👏", "⭐"];
                  return (
                    <div key={r.id} style={{ ...card, display: "grid", gap: "10px" }}>
                      {/* Avatar + name + content row */}
                      <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                        {r.userAvatar ? (
                          <img src={r.userAvatar} alt={r.userName} style={{ width: "36px", height: "36px", borderRadius: "999px", objectFit: "cover", border: "2px solid #bfdbfe", flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: "36px", height: "36px", borderRadius: "999px", background: "#bfdbfe", flexShrink: 0 }} />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px", marginBottom: "4px" }}>{r.userName || "Client"}</div>
                          {b.type === "announcement" && (
                            <div style={{ color: "#6b8cb0", fontSize: "13px" }}>✓ Read · {formatDate(r.submittedAt)}</div>
                          )}
                          {b.type === "question" && (
                            <RichTextDisplay html={r.comment} style={{ color: "#243b53", fontSize: "14px" }} />
                          )}
                        </div>
                      </div>

                      {/* Reaction bar */}
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                          {REACTIONS.map((emoji) => (
                            <button
                              key={emoji}
                              onClick={() => handleReact(r.id, emoji)}
                              style={{
                                padding: "4px 10px", borderRadius: "999px", border: r.adminReaction === emoji ? "2px solid #2563eb" : "2px solid #bfdbfe",
                                background: r.adminReaction === emoji ? "#e0f2fe" : "#f5faff",
                                cursor: "pointer", fontSize: "16px", lineHeight: 1,
                              }}
                            >{emoji}</button>
                          ))}
                        </div>
                        <button
                          onClick={() => setReply(r.id, { open: !replyOpen, text: replyText })}
                          style={{ ...btnSecondary, fontSize: "12px", padding: "4px 12px", flexShrink: 0 }}
                        >
                          {r.adminReply ? "✏️ Edit reply" : "💬 Reply"}
                        </button>
                      </div>

                      {/* Existing reply display */}
                      {r.adminReply && !replyOpen && (
                        <div style={{ background: "#f0f9ff", borderRadius: "12px", padding: "10px 14px", border: "2px solid #bfdbfe", fontSize: "13px", color: "#1e3a5f" }}>
                          <span style={{ fontWeight: "bold", color: "#2563eb", marginRight: "6px" }}>You:</span>
                          <RichTextDisplay html={r.adminReply} style={{ display: "inline", color: "#1e3a5f" }} />
                          <button onClick={() => handleDeleteReply(r.id)} style={{ marginLeft: "10px", background: "none", border: "none", color: "#93c5fd", cursor: "pointer", fontSize: "12px" }}>✕ Remove</button>
                        </div>
                      )}

                      {/* Client's reply back */}
                      {r.clientReply && (
                        <div style={{ background: "#f8fbff", borderRadius: "12px", padding: "10px 14px", border: "1.5px solid #dbeafe", fontSize: "13px", color: "#243b53" }}>
                          <span style={{ fontWeight: "bold", color: "#4b6b8a", marginRight: "6px" }}>{r.userName || "Client"}:</span>
                          <RichTextDisplay html={r.clientReply} style={{ display: "inline", color: "#243b53" }} />
                        </div>
                      )}

                      {/* Reply input */}
                      {replyOpen && (
                        <div style={{ display: "grid", gap: "8px" }}>
                          <RichTextEditor
                            value={replyText}
                            onChange={(v) => setReply(r.id, { text: v })}
                            placeholder="Write a reply..."
                            rows={2}
                          />
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button onClick={() => handleReply(r.id)} disabled={rs.saving || !replyText.trim()} style={{ ...btnPrimary, opacity: rs.saving || !replyText.trim() ? 0.6 : 1, fontSize: "13px", padding: "8px 16px" }}>
                              {rs.saving ? "Saving..." : "Send reply"}
                            </button>
                            <button onClick={() => setReply(r.id, { open: false })} style={{ ...btnSecondary, fontSize: "13px" }}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ── Main list view ────────────────────────────────────────────────────────

  return (
    <div style={{ display: "grid", gap: "14px" }}>
      {/* Program selector + new button */}
      <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
        <select
          value={selectedProgram}
          onChange={(e) => { setSelectedProgram(e.target.value); setShowForm(false); }}
          style={{ ...inputStyle, width: "auto", flex: 1 }}
        >
          <option value="all">All Programs</option>
          {programTags.map((tag) => {
            const prog = programOptions.find((p) => (p.value || p.tag) === tag);
            return <option key={tag} value={tag}>{prog ? prog.label : tag}</option>;
          })}
        </select>
        <button onClick={() => setShowForm((p) => !p)} style={btnPrimary}>
          {showForm ? "✕ Cancel" : "＋ New Broadcast"}
        </button>
        <button onClick={() => { setShowScheduleCall((p) => !p); setScStatus(""); }} style={{ ...btnPrimary, background: "#059669" }}>
          {showScheduleCall ? "✕ Cancel" : "📹 Call"}
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div id="admin-broadcasts-form" style={{ ...card, display: "grid", gap: "14px" }}>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px" }}>New Broadcast</div>

          {/* Send to section */}
          {type !== "note" && (
            <div style={{ display: "grid", gap: "10px" }}>
              <div>
                <div style={labelStyle}>Send to</div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {[{ key: "program", label: "Whole program" }, { key: "users", label: "Specific people" }].map((opt) => (
                    <button key={opt.key} onClick={() => { setAudienceType(opt.key); setTargetUserIds([]); setTargetUserSearch(""); }} style={{ padding: "8px 14px", borderRadius: "12px", border: audienceType === opt.key ? "2px solid #2563eb" : "2px solid #bfdbfe", background: audienceType === opt.key ? "#e0f2fe" : "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {audienceType === "program" && selectedProgram === "all" && (
                <select value={formProgram} onChange={(e) => setFormProgram(e.target.value)} style={inputStyle}>
                  <option value="">Select a program...</option>
                  {programTags.map((tag) => { const prog = programOptions.find((p) => (p.value || p.tag) === tag); return <option key={tag} value={tag}>{prog ? prog.label : tag}</option>; })}
                </select>
              )}
              {audienceType === "users" && (
                <div>
                  <div style={{ position: "relative" }}>
                    <input type="text" value={targetUserSearch} onChange={(e) => setTargetUserSearch(e.target.value)} placeholder="Search users to add..." style={inputStyle} />
                    {targetUserResults.length > 0 && (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", borderRadius: "12px", border: "2px solid #bfdbfe", boxShadow: "0 8px 20px rgba(30,58,138,0.12)", zIndex: 100, marginTop: "4px", overflow: "hidden" }}>
                        {targetUserResults.map((u) => (
                          <button key={u.id} type="button" onClick={() => { setTargetUserIds([...targetUserIds, u.id]); setTargetUserSearch(""); }} style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "10px 14px", background: "transparent", border: "none", borderBottom: "1px solid #f0f9ff", cursor: "pointer", textAlign: "left" }}>
                            {u.avatar ? <img src={u.avatar} alt={u.name} style={{ width: "28px", height: "28px", borderRadius: "999px", objectFit: "cover" }} /> : <div style={{ width: "28px", height: "28px", borderRadius: "999px", background: "#e0f2fe" }} />}
                            <span style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "13px" }}>{u.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {targetUserIds.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
                      {(allUsers || []).filter((u) => targetUserIds.includes(u.id)).map((u) => (
                        <div key={u.id} style={{ display: "flex", alignItems: "center", gap: "5px", background: "#e0f2fe", borderRadius: "999px", padding: "4px 8px 4px 4px", border: "2px solid #bfdbfe" }}>
                          {u.avatar ? <img src={u.avatar} alt={u.name} style={{ width: "20px", height: "20px", borderRadius: "999px", objectFit: "cover" }} /> : <div style={{ width: "20px", height: "20px", borderRadius: "999px", background: "#bfdbfe" }} />}
                          <span style={{ fontSize: "12px", fontWeight: "bold", color: "#1e3a5f" }}>{u.name}</span>
                          <button type="button" onClick={() => setTargetUserIds(targetUserIds.filter((id) => id !== u.id))} style={{ background: "none", border: "none", color: "#4b6b8a", cursor: "pointer", padding: "0", lineHeight: 1, fontSize: "13px" }}>x</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Type selector */}
          <div>
            <div style={labelStyle}>Type</div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setType(t.key)}
                  style={{
                    padding: "8px 14px", borderRadius: "14px", cursor: "pointer", fontWeight: "bold", fontSize: "13px",
                    border: type === t.key ? "2px solid #2563eb" : "2px solid #bfdbfe",
                    background: type === t.key ? "#e0f2fe" : "#f5faff",
                    color: "#1e3a5f",
                  }}
                >
                  {t.emoji} {t.label}
                </button>
              ))}
            </div>
            <div style={{ color: "#6b8cb0", fontSize: "12px", marginTop: "6px" }}>
              {TYPES.find((t) => t.key === type)?.desc}
            </div>
          </div>

          {/* Title */}
          <div>
            <div style={labelStyle}>Title</div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Week 2 Check-in" style={inputStyle} />
          </div>

          {/* Body */}
          <div>
            <div style={labelStyle}>{type === "question" ? "Question" : type === "poll" ? "Question / Instructions" : "Message"}</div>
            <RichTextEditor
              value={body}
              onChange={setBody}
              placeholder={type === "announcement" ? "Write your message..." : "Write your question..."}
              rows={4}
            />
          </div>

          {/* Note-specific fields */}
          {type === "note" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <div style={labelStyle}>Start Date</div>
                  <input type="date" value={noteStartDate} onChange={(e) => setNoteStartDate(e.target.value)} style={inputStyle} />
                  <input type="time" step="300" value={noteStartTime} onChange={(e) => setNoteStartTime(e.target.value)} style={{ ...inputStyle, marginTop: "6px" }} />
                </div>
                <div>
                  <div style={labelStyle}>End Date</div>
                  <input type="date" value={noteEndDate} onChange={(e) => setNoteEndDate(e.target.value)} style={inputStyle} />
                  <input type="time" step="300" value={noteEndTime} onChange={(e) => setNoteEndTime(e.target.value)} style={{ ...inputStyle, marginTop: "6px" }} />
                </div>
              </div>
              <div>
                <div style={labelStyle}>Show To</div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
                  {[{ value: "all", label: "All Users" }, { value: "program", label: "A Program" }, { value: "user", label: "One Person" }].map(({ value, label }) => (
                    <button key={value} type="button" onClick={() => setNoteTargetType(value)} style={{ padding: "8px 14px", borderRadius: "12px", border: noteTargetType === value ? "2px solid #2563eb" : "2px solid #bfdbfe", background: noteTargetType === value ? "#e0f2fe" : "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}>
                      {label}
                    </button>
                  ))}
                </div>
                {noteTargetType === "program" && (
                  <select value={noteTargetProgram} onChange={(e) => setNoteTargetProgram(e.target.value)} style={inputStyle}>
                    <option value="">Select a program…</option>
                    {programTags.map((tag) => { const p = programOptions.find((p) => (p.value || p.tag) === tag); return <option key={tag} value={tag}>{p ? p.label : tag}</option>; })}
                  </select>
                )}
                {noteTargetType === "user" && (
                  <div style={{ position: "relative" }}>
                    <input type="text" value={noteUserSearch} onChange={(e) => { setNoteUserSearch(e.target.value); if (!e.target.value.trim()) { setNoteTargetUserId(""); setNoteTargetUserName(""); } }} placeholder="Start typing a name…" style={inputStyle} />
                    {noteTargetUserId && <div style={{ marginTop: "6px", padding: "8px 12px", borderRadius: "10px", background: "#e0f2fe", border: "1px solid #93c5fd", color: "#1e3a5f", fontSize: "13px", fontWeight: "bold" }}>✓ {noteTargetUserName} <button type="button" onClick={() => { setNoteTargetUserId(""); setNoteTargetUserName(""); setNoteUserSearch(""); }} style={{ marginLeft: "8px", background: "none", border: "none", color: "#4b6b8a", cursor: "pointer" }}>✕</button></div>}
                    {noteUserResults.length > 0 && !noteTargetUserId && (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", borderRadius: "12px", border: "2px solid #bfdbfe", boxShadow: "0 8px 20px rgba(30,58,138,0.12)", zIndex: 100, marginTop: "4px", overflow: "hidden" }}>
                        {noteUserResults.map((u) => (
                          <button key={u.id} type="button" onClick={() => { setNoteTargetUserId(u.id); setNoteTargetUserName(u.name); setNoteUserSearch(u.name); }} style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "10px 14px", background: "transparent", border: "none", borderBottom: "1px solid #f0f9ff", cursor: "pointer", textAlign: "left" }}>
                            {u.avatar ? <img src={u.avatar} alt={u.name} style={{ width: "28px", height: "28px", borderRadius: "999px", objectFit: "cover" }} /> : <div style={{ width: "28px", height: "28px", borderRadius: "999px", background: "#e0f2fe" }} />}
                            <div style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "13px" }}>{u.name}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div style={{ background: "#f0f9ff", borderRadius: "12px", padding: "12px 14px", border: "1px solid #bfdbfe" }}>
                <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "13px", marginBottom: "8px" }}>🔗 Optional Link</div>
                <input type="text" value={noteLinkLabel} onChange={(e) => setNoteLinkLabel(e.target.value)} placeholder="Button label (e.g. Join Group Call)" style={{ ...inputStyle, marginBottom: "6px" }} />
                <input type="url" value={noteLinkUrl} onChange={(e) => setNoteLinkUrl(e.target.value)} placeholder="https://…" style={inputStyle} />
              </div>
            </>
          )}

          {/* Poll options */}
          {type === "poll" && (
            <div>
              <div style={labelStyle}>Options</div>
              <div style={{ display: "grid", gap: "8px" }}>
                {options.map((opt, i) => (
                  <div key={i} style={{ display: "flex", gap: "8px" }}>
                    <input
                      value={opt}
                      onChange={(e) => {
                        const next = [...options];
                        next[i] = e.target.value;
                        setOptions(next);
                      }}
                      placeholder={`Option ${i + 1}`}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    {options.length > 2 && (
                      <button onClick={() => setOptions(options.filter((_, j) => j !== i))} style={{ ...btnSecondary, color: "#38bdf8", borderColor: "#38bdf8" }}>✕</button>
                    )}
                  </div>
                ))}
                <button onClick={() => setOptions([...options, ""])} style={{ ...btnSecondary, alignSelf: "flex-start" }}>+ Add option</button>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px", cursor: "pointer", color: "#1e3a5f", fontSize: "14px" }}>
                <input type="checkbox" checked={allowMultiple} onChange={(e) => setAllowMultiple(e.target.checked)} />
                Allow selecting multiple answers
              </label>
            </div>
          )}

          {/* Call poll time slots */}
          {type === "call_poll" && (
            <div>
              <div style={labelStyle}>Time Slots</div>
              <div style={{ color: "#6b8cb0", fontSize: "12px", marginBottom: "8px" }}>Add up to 4 time options for your group to vote on</div>
              <div style={{ display: "grid", gap: "8px" }}>
                {callTimeSlots.map((slot, i) => (
                  <div key={i} style={{ display: "flex", gap: "8px" }}>
                    <input
                      type="datetime-local"
                      value={slot}
                      onChange={(e) => {
                        const next = [...callTimeSlots];
                        next[i] = e.target.value;
                        setCallTimeSlots(next);
                      }}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    {callTimeSlots.length > 2 && (
                      <button onClick={() => setCallTimeSlots(callTimeSlots.filter((_, j) => j !== i))} style={{ ...btnSecondary, color: "#38bdf8", borderColor: "#38bdf8" }}>✕</button>
                    )}
                  </div>
                ))}
                {callTimeSlots.length < 4 && (
                  <button onClick={() => setCallTimeSlots([...callTimeSlots, ""])} style={{ ...btnSecondary, alignSelf: "flex-start" }}>+ Add time slot</button>
                )}
              </div>
            </div>
          )}

          {/* XP reward toggle — not applicable for notes */}
          {type !== "note" && <div style={{ background: "#f0f9ff", borderRadius: "14px", padding: "14px", border: "2px solid #bfdbfe" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: xpEnabled ? "12px" : 0 }}>
              <div>
                <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px" }}>💎 XP Reward</div>
                <div style={{ color: "#6b8cb0", fontSize: "12px", marginTop: "2px" }}>
                  Award XP when users respond to this broadcast
                </div>
              </div>
              <button
                type="button"
                onClick={() => setXpEnabled((p) => !p)}
                style={{ width: "52px", height: "28px", borderRadius: "999px", border: "none", background: xpEnabled ? "#2563eb" : "#d1c4e9", cursor: "pointer", position: "relative", flexShrink: 0 }}
              >
                <div style={{ position: "absolute", top: "3px", left: xpEnabled ? "27px" : "3px", width: "22px", height: "22px", borderRadius: "999px", background: "#fff", transition: "left 0.2s" }} />
              </button>
            </div>
            {xpEnabled && (
              <div>
                <div style={labelStyle}>XP amount</div>
                <input
                  type="number"
                  min="1"
                  value={xpReward}
                  onChange={(e) => setXpReward(Math.max(1, Number(e.target.value)))}
                  style={inputStyle}
                />
              </div>
            )}
          </div>}

          {type === "note" ? (
            <button
              onClick={saveNote}
              disabled={saving || !title.trim() || !body.trim() || (noteTargetType === "user" && !noteTargetUserId)}
              style={{ ...btnPrimary, background: "#38bdf8", opacity: saving || !title.trim() || !body.trim() || (noteTargetType === "user" && !noteTargetUserId) ? 0.6 : 1 }}
            >
              {saving ? "Saving…" : "📌 Pin Note"}
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={saving || !title.trim() || !body.trim() || (audienceType === "program" && selectedProgram === "all" && !formProgram) || (audienceType === "users" && targetUserIds.length === 0)}
              style={{ ...btnPrimary, opacity: saving || !title.trim() || !body.trim() || (selectedProgram === "all" && !formProgram) ? 0.6 : 1 }}
            >
              {saving ? "Sending..." : audienceType === "users" ? ("Send to " + targetUserIds.length + " person" + (targetUserIds.length !== 1 ? "s" : "")) : ("Send to " + (selectedProgram === "all" ? (formProgram || "...") : selectedProgram))}
            </button>
          )}
        </div>
      )}

      {/* Active call banner */}
      {groupCall && (getCallStatus(groupCall) === "live" || getCallStatus(groupCall) === "scheduled") && (
        <div style={{ ...card, background: "#ecfdf5", border: "2px solid #6ee7b7", display: "grid", gap: "10px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: "bold", color: "#065f46", fontSize: "15px" }}>
                {getCallStatus(groupCall) === "live" ? "🟢 Call is live" : "📅 Call scheduled"}
              </div>
              <div style={{ color: "#047857", fontSize: "13px" }}>{groupCall.title}</div>
            </div>
          </div>
          {groupCall.roomUrl && (
            <a
              href={groupCall.roomUrl + (adminName ? "?name=" + encodeURIComponent(adminName) : "")}
              target="_blank"
              rel="noreferrer"
              style={{ display: "block", textAlign: "center", padding: "10px", borderRadius: "12px", background: "#059669", color: "#fff", fontWeight: "bold", fontSize: "14px", textDecoration: "none" }}
            >
              📹 Join Call
            </a>
          )}
          <button
            onClick={async () => {
              setEndingLiveCall(true);
              try { await deleteDailyRoom(groupCall.roomName); } catch (_) {}
              await endCall(groupCall.id);
              setEndingLiveCall(false);
            }}
            disabled={endingLiveCall}
            style={{ padding: "10px", borderRadius: "12px", background: "transparent", color: "#dc2626", border: "2px solid #fca5a5", fontWeight: "bold", fontSize: "14px", cursor: endingLiveCall ? "default" : "pointer" }}
          >
            {endingLiveCall ? "Ending..." : "End Call for Everyone"}
          </button>
        </div>
      )}

      {/* Schedule a Call form */}
      {showScheduleCall && (
        <div style={{ ...card, display: "grid", gap: "14px" }}>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px" }}>📹 Start or Schedule a Call</div>
          <div>
            <div style={labelStyle}>Title</div>
            <input value={scTitle} onChange={(e) => setScTitle(e.target.value)} placeholder="e.g. Weekly Check-in" style={inputStyle} />
          </div>
          <div>
            <div style={labelStyle}>When</div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {[{ key: "now", label: "Start now" }, { key: "future", label: "Schedule for..." }].map((opt) => (
                <button key={opt.key} onClick={() => setScWhen(opt.key)} style={{ padding: "8px 14px", borderRadius: "12px", border: scWhen === opt.key ? "2px solid #059669" : "2px solid #bfdbfe", background: scWhen === opt.key ? "#dcfce7" : "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}>
                  {opt.label}
                </button>
              ))}
            </div>
            {scWhen === "future" && (
              <input type="datetime-local" value={scDateTime} onChange={(e) => setScDateTime(e.target.value)} style={{ ...inputStyle, marginTop: "8px" }} />
            )}
          </div>
          <div>
            <div style={labelStyle}>Join window closes (optional)</div>
            <input type="datetime-local" value={scEndDateTime} onChange={(e) => setScEndDateTime(e.target.value)} style={inputStyle} />
            <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>After this time the Join button disappears, but the call keeps going until you end it.</div>
          </div>
          <div>
            <div style={labelStyle}>Who</div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {[{ key: "program", label: "Entire program" }, { key: "users", label: "Specific people" }].map((opt) => (
                <button key={opt.key} onClick={() => { setScAudience(opt.key); setScTargetUserIds([]); setScTargetUserSearch(""); }} style={{ padding: "8px 14px", borderRadius: "12px", border: scAudience === opt.key ? "2px solid #059669" : "2px solid #bfdbfe", background: scAudience === opt.key ? "#dcfce7" : "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}>
                  {opt.label}
                </button>
              ))}
            </div>
            {scAudience === "program" && (
              <select value={scProgram} onChange={(e) => setScProgram(e.target.value)} style={{ ...inputStyle, marginTop: "8px" }}>
                <option value="">Select a program...</option>
                {programTags.map((tag) => { const p = programOptions.find((p) => (p.value || p.tag) === tag); return <option key={tag} value={tag}>{p ? p.label : tag}</option>; })}
              </select>
            )}
            {scAudience === "users" && (
              <div style={{ marginTop: "8px" }}>
                <div style={{ position: "relative" }}>
                  <input type="text" value={scTargetUserSearch} onChange={(e) => setScTargetUserSearch(e.target.value)} placeholder="Search users to invite..." style={inputStyle} />
                  {scUserResults.length > 0 && (
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", borderRadius: "12px", border: "2px solid #bfdbfe", boxShadow: "0 8px 20px rgba(30,58,138,0.12)", zIndex: 100, marginTop: "4px", overflow: "hidden" }}>
                      {scUserResults.map((u) => (
                        <button key={u.id} type="button" onClick={() => { setScTargetUserIds([...scTargetUserIds, u.id]); setScTargetUserSearch(""); }} style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "10px 14px", background: "transparent", border: "none", borderBottom: "1px solid #f0f9ff", cursor: "pointer", textAlign: "left" }}>
                          {u.avatar ? <img src={u.avatar} alt={u.name} style={{ width: "28px", height: "28px", borderRadius: "999px", objectFit: "cover" }} /> : <div style={{ width: "28px", height: "28px", borderRadius: "999px", background: "#e0f2fe" }} />}
                          <span style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "13px" }}>{u.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {scTargetUserIds.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
                    {(allUsers || []).filter((u) => scTargetUserIds.includes(u.id)).map((u) => (
                      <div key={u.id} style={{ display: "flex", alignItems: "center", gap: "5px", background: "#dcfce7", borderRadius: "999px", padding: "4px 8px 4px 4px", border: "2px solid #86efac" }}>
                        {u.avatar ? <img src={u.avatar} alt={u.name} style={{ width: "20px", height: "20px", borderRadius: "999px", objectFit: "cover" }} /> : <div style={{ width: "20px", height: "20px", borderRadius: "999px", background: "#86efac" }} />}
                        <span style={{ fontSize: "12px", fontWeight: "bold", color: "#1e3a5f" }}>{u.name}</span>
                        <button type="button" onClick={() => setScTargetUserIds(scTargetUserIds.filter((id) => id !== u.id))} style={{ background: "none", border: "none", color: "#4b6b8a", cursor: "pointer", padding: "0", lineHeight: 1, fontSize: "13px" }}>x</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <div style={labelStyle}>Screen sharing</div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {[
                { value: "everyone", label: "Everyone can share" },
                { value: "admin", label: "Only you (admin)" },
                { value: "none", label: "Disabled" },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setScScreenshare(value)}
                  style={{ padding: "6px 12px", borderRadius: "10px", border: "2px solid " + (scScreenshare === value ? "#2563eb" : "#dbeafe"), background: scScreenshare === value ? "#dbeafe" : "#f5faff", color: "#1e3a5f", fontSize: "13px", fontWeight: scScreenshare === value ? "bold" : "normal", cursor: "pointer" }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={handleScheduleCall}
            disabled={scSaving || !scTitle.trim() || (scWhen === "future" && !scDateTime) || (scAudience === "program" && !scProgram) || (scAudience === "users" && scTargetUserIds.length === 0)}
            style={{ ...btnPrimary, background: "#059669", opacity: scSaving || !scTitle.trim() || (scWhen === "future" && !scDateTime) || (scAudience === "program" && !scProgram) || (scAudience === "users" && scTargetUserIds.length === 0) ? 0.6 : 1 }}
          >
            {scSaving ? "Setting up..." : scWhen === "now" ? "▶ Start Call Now" : "📅 Schedule Call"}
          </button>
          {scStatus && (
            <div style={{ padding: "10px 14px", borderRadius: "10px", background: scStatus.startsWith("Error") ? "#fef2f2" : "#ecfdf5", border: "2px solid " + (scStatus.startsWith("Error") ? "#fca5a5" : "#6ee7b7"), color: scStatus.startsWith("Error") ? "#dc2626" : "#059669", fontWeight: "bold", fontSize: "13px" }}>
              {scStatus}
            </div>
          )}
          {scRoomUrl && !scStatus.startsWith("Error") && (
            <a
              href={scRoomUrl + (adminName ? "?name=" + encodeURIComponent(adminName) : "")}
              target="_blank"
              rel="noreferrer"
              style={{ display: "block", textAlign: "center", padding: "12px", borderRadius: "14px", background: "#059669", color: "#fff", fontWeight: "bold", fontSize: "15px", textDecoration: "none" }}
            >
              📹 Join Call Now
            </a>
          )}
        </div>
      )}

      {/* Broadcasts list */}
      {loading ? (
        <div style={{ color: "#4b6b8a", padding: "16px" }}>Loading...</div>
      ) : filteredBroadcasts.length === 0 ? (
        <div style={{ ...card, color: "#4b6b8a" }}>{selectedProgram === "all" ? "No broadcasts yet." : `No broadcasts sent to ${selectedProgram} yet.`}</div>
      ) : (
        filteredBroadcasts.map((b) => {
          const typeInfo = TYPES.find((t) => t.key === b.type);
          return (
            <button
              key={b.id}
              onClick={() => openBroadcast(b)}
              style={{
                ...card,
                width: "100%",
                cursor: "pointer",
                textAlign: "left",
                border: b.isActive ? "2px solid #bfdbfe" : "2px solid #dbeafe",
                opacity: b.isActive ? 1 : 0.7,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "16px" }}>{typeInfo?.emoji}</span>
                  <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>{b.title}</span>
                </div>
                <span style={{ fontSize: "11px", background: b.isActive ? "#e0f2fe" : "#dbeafe", color: "#4b6b8a", padding: "2px 8px", borderRadius: "999px", border: "2px solid #bfdbfe", flexShrink: 0 }}>
                  {b.isActive ? "Active" : "Closed"}
                </span>
              </div>
              <RichTextDisplay html={b.body} style={{ color: "#4b6b8a", fontSize: "13px", marginTop: "6px" }} />
              <div style={{ color: "#6b8cb0", fontSize: "12px", marginTop: "8px" }}>{typeInfo?.label} · {formatDate(b.createdAt)}</div>
            </button>
          );
        })
      )}

      {/* Pinned Notes section */}
      {notes.length > 0 && (
        <div style={{ display: "grid", gap: "8px" }}>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px", marginTop: "4px" }}>📌 Pinned Notes</div>
          {notes.map((note) => {
            const now = new Date();
            const start = new Date(`${note.startDate}T${note.startTime || "00:00"}`);
            const end = new Date(`${note.endDate}T${note.endTime || "23:59"}`);
            const active = start <= now && end >= now;
            return (
              <div key={note.id} style={{ background: active ? "#f0f9ff" : "#f8fbff", borderRadius: "14px", padding: "14px 16px", border: active ? "2px solid #38bdf8" : "2px solid #bfdbfe" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
                      <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px" }}>{note.title}</span>
                      <span style={{ fontSize: "11px", padding: "1px 7px", borderRadius: "999px", background: active ? "#dcfce7" : "#f1f5f9", color: active ? "#16a34a" : "#64748b", border: active ? "1px solid #86efac" : "1px solid #cbd5e1" }}>{active ? "● Active" : "○ Inactive"}</span>
                      {note.targetType === "user" && <span style={{ fontSize: "11px", color: "#6b8cb0" }}>👤 {note.targetUserName}</span>}
                      {note.targetType === "program" && <span style={{ fontSize: "11px", color: "#6b8cb0" }}>{note.targetProgram}</span>}
                    </div>
                    <div style={{ color: "#4b6b8a", fontSize: "13px", lineHeight: 1.5 }}>{note.body}</div>
                    <div style={{ color: "#9ca3af", fontSize: "11px", marginTop: "4px" }}>{note.startDate} → {note.endDate}{note.linkUrl && <span style={{ marginLeft: "8px" }}>🔗 {note.linkLabel || note.linkUrl}</span>}</div>
                  </div>
                  <button type="button" onClick={() => deleteNote(note.id)} style={{ padding: "4px 10px", borderRadius: "8px", border: "none", background: "#fca5a5", color: "#fff", fontWeight: "bold", fontSize: "12px", cursor: "pointer", flexShrink: 0 }}>✕</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
